/**
 * Admin · Vendors & Collaborations.
 *
 * Internal CRUD for external partners (caterers, photographers, health
 * screeners, etc.) and the scheduled engagements with them.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Building2, Calendar, X, Pencil, Archive } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader, AdminEmpty } from "./_shared";

const TYPE_KEYS = [
  "caterer", "photographer", "health", "cleaning", "maintenance",
  "field_trip", "supplies", "training", "consultant", "other",
];

const KIND_KEYS = [
  "meal_delivery", "photo_session", "health_screening", "cleaning",
  "maintenance", "field_trip", "training", "supply_delivery", "other",
];

const STATUS_PALETTE = {
  active: "bg-[#D1F1D6] text-[#1F3A2A]",
  paused: "bg-[#F4E5C5] text-[#7A5E26]",
  ended: "bg-[#EEF0F4] text-[#525252]",
  scheduled: "bg-[#E6EFE8] text-[#1F3A2A]",
  in_progress: "bg-[#F4E5C5] text-[#7A5E26]",
  completed: "bg-[#D1F1D6] text-[#1F3A2A]",
  cancelled: "bg-[#FCE2E2] text-[#A04545]",
};

function fmtWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function AdminVendors() {
  const { t } = useI18n();
  const [vendors, setVendors] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("vendors");
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [collabOpen, setCollabOpen] = useState(null);

  const typeOptions = useMemo(
    () => TYPE_KEYS.map((k) => ({ value: k, label: t(`admin_panel.vendors.type_${k}`) })),
    [t]
  );
  const kindOptions = useMemo(
    () => KIND_KEYS.map((k) => ({ value: k, label: t(`admin_panel.collaborations.kind_${k}`) })),
    [t]
  );

  const reload = async () => {
    setLoading(true);
    try {
      const [vr, cr] = await Promise.all([
        api.get("/admin/vendors"),
        api.get("/admin/collaborations", { params: { limit: 200 } }),
      ]);
      setVendors(vr.data || []);
      setCollabs(cr.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
  }, []);

  const upcoming = useMemo(
    () =>
      collabs
        .filter((c) => ["scheduled", "in_progress"].includes(c.status))
        .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")),
    [collabs]
  );

  const removeVendor = async (v) => {
    if (!window.confirm(t("admin_panel.vendors.confirm_archive", { name: v.name }))) return;
    try {
      await api.delete(`/admin/vendors/${v.id}`);
      toast.success(t("admin_panel.vendors.toast_archived"));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("admin_panel.vendors.toast_archive_failed"));
    }
  };

  const typeLabel = (key) => t(`admin_panel.vendors.type_${key}`) || key;
  const statusLabel = (status) => {
    const map = {
      active: "admin_panel.vendors.status_active",
      paused: "admin_panel.vendors.status_paused",
      ended: "admin_panel.vendors.status_ended",
      scheduled: "admin_panel.collaborations.status_scheduled",
      in_progress: "admin_panel.collaborations.status_in_progress",
      completed: "admin_panel.collaborations.status_completed",
      cancelled: "admin_panel.collaborations.status_cancelled",
    };
    return t(map[status] || "") || status;
  };

  return (
    <div data-testid="admin-vendors-page" className="space-y-7">
      <AdminHeader
        eyebrow={t("admin_panel.vendors.eyebrow")}
        title={t("admin_panel.vendors.title")}
        action={
          <button
            type="button"
            onClick={() => {
              setEditingVendor(null);
              setComposerOpen(true);
            }}
            data-testid="admin-vendors-add"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#132619] text-white text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#0b2117]"
          >
            <Plus size={13} strokeWidth={1.8} />
            {t("admin_panel.vendors.new_vendor")}
          </button>
        }
      />

      <div className="inline-flex bg-[#FAF6EE] rounded-full p-1">
        <button
          type="button"
          data-testid="admin-vendors-tab-vendors"
          onClick={() => setTab("vendors")}
          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
            tab === "vendors" ? "bg-white shadow text-[#171717]" : "text-[#525252]"
          }`}
        >
          {t("admin_panel.vendors.tab_vendors")} ({vendors.length})
        </button>
        <button
          type="button"
          data-testid="admin-vendors-tab-collabs"
          onClick={() => setTab("collabs")}
          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
            tab === "collabs" ? "bg-white shadow text-[#171717]" : "text-[#525252]"
          }`}
        >
          {t("admin_panel.vendors.tab_collaborations")} ({collabs.length})
        </button>
      </div>

      {loading ? (
        <p className="font-serif italic text-[#525252]">{t("admin_panel.vendors.loading")}</p>
      ) : tab === "vendors" ? (
        vendors.length === 0 ? (
          <AdminEmpty testid="admin-vendors-empty">
            {t("admin_panel.vendors.empty_state")}
          </AdminEmpty>
        ) : (
          <ul className="rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6">
            {vendors.map((v) => (
              <li
                key={v.id}
                data-testid={`admin-vendor-row-${v.id}`}
                className="px-5 py-4 flex items-start gap-4"
              >
                <span className="h-10 w-10 rounded-2xl bg-[#E6EFE8] flex items-center justify-center shrink-0">
                  <Building2 size={16} strokeWidth={1.6} className="text-[#1F3A2A]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline flex-wrap gap-2">
                    <span className="font-serif text-base text-[#171717]">{v.name}</span>
                    <span className="text-[10px] tracking-[0.18em] uppercase text-[#525252]">
                      {typeLabel(v.vendor_type)}
                    </span>
                    <span
                      className={`text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full ${STATUS_PALETTE[v.status] || ""}`}
                    >
                      {statusLabel(v.status)}
                    </span>
                  </div>
                  <div className="text-xs text-[#525252] mt-1 flex flex-wrap gap-3">
                    {v.contact_name && <span>{v.contact_name}</span>}
                    {v.contact_email && <span>{v.contact_email}</span>}
                    {v.contact_phone && <span>{v.contact_phone}</span>}
                    <span>
                      {t("admin_panel.vendors.collab_count", { count: v.collaborations_count })}
                    </span>
                  </div>
                  {v.notes && (
                    <p className="text-xs text-[#525252] mt-1 italic">{v.notes}</p>
                  )}
                </div>
                <div className="shrink-0 flex gap-1">
                  <button
                    type="button"
                    onClick={() => setCollabOpen(v)}
                    data-testid={`admin-vendor-schedule-${v.id}`}
                    className="h-8 px-3 rounded-full bg-[#F2EFEA] hover:bg-[#E6EFE8] text-xs text-[#1F3A2A] inline-flex items-center gap-1.5"
                  >
                    <Calendar size={12} strokeWidth={1.6} />
                    {t("admin_panel.vendors.schedule")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingVendor(v);
                      setComposerOpen(true);
                    }}
                    data-testid={`admin-vendor-edit-${v.id}`}
                    aria-label={t("admin_panel.vendors.edit")}
                    className="h-8 w-8 rounded-full bg-[#F2EFEA] hover:bg-[#EAE5EF] flex items-center justify-center text-[#525252]"
                  >
                    <Pencil size={13} strokeWidth={1.6} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeVendor(v)}
                    data-testid={`admin-vendor-archive-${v.id}`}
                    aria-label={t("admin_panel.vendors.archive")}
                    className="h-8 w-8 rounded-full bg-[#F2EFEA] hover:bg-[#FCE2E2] flex items-center justify-center text-[#525252]"
                  >
                    <Archive size={13} strokeWidth={1.6} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )
      ) : (
        <CollabList collabs={collabs} upcoming={upcoming} t={t} statusLabel={statusLabel} />
      )}

      {composerOpen && (
        <VendorComposer
          t={t}
          typeOptions={typeOptions}
          initial={editingVendor}
          onClose={() => {
            setComposerOpen(false);
            setEditingVendor(null);
          }}
          onSaved={() => {
            setComposerOpen(false);
            setEditingVendor(null);
            reload();
          }}
        />
      )}

      {collabOpen && (
        <CollabComposer
          t={t}
          kindOptions={kindOptions}
          vendor={collabOpen}
          onClose={() => setCollabOpen(null)}
          onSaved={() => {
            setCollabOpen(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function VendorComposer({ initial, onClose, onSaved, t, typeOptions }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    vendor_type: initial?.vendor_type || "caterer",
    contact_name: initial?.contact_name || "",
    contact_email: initial?.contact_email || "",
    contact_phone: initial?.contact_phone || "",
    address: initial?.address || "",
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        vendor_type: form.vendor_type,
        contact_name: form.contact_name.trim() || null,
        contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
      };
      if (initial?.id) {
        await api.patch(`/admin/vendors/${initial.id}`, payload);
        toast.success(t("admin_panel.vendors.toast_updated"));
      } else {
        await api.post("/admin/vendors", payload);
        toast.success(t("admin_panel.vendors.toast_added"));
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("admin_panel.vendors.toast_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" data-testid="vendor-composer">
      <form
        onSubmit={submit}
        className="bg-white rounded-3xl w-full max-w-lg p-6 space-y-5 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between">
          <h3 className="font-serif text-2xl text-[#171717]">
            {initial?.id
              ? t("admin_panel.vendors.composer_title_edit")
              : t("admin_panel.vendors.composer_title_new")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("admin_panel.vendors.close")}
            data-testid="vendor-composer-close"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="sm:col-span-2 space-y-1.5">
            <span className="eyebrow">{t("admin_panel.vendors.field_name")}</span>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="vendor-form-name"
              className="orbit-input"
            />
          </label>
          <label className="space-y-1.5">
            <span className="eyebrow">{t("admin_panel.vendors.field_type")}</span>
            <select
              value={form.vendor_type}
              onChange={(e) => setForm({ ...form, vendor_type: e.target.value })}
              data-testid="vendor-form-type"
              className="orbit-input bg-transparent"
            >
              {typeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="eyebrow">{t("admin_panel.vendors.field_contact_name")}</span>
            <input
              type="text"
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              className="orbit-input"
            />
          </label>
          <label className="space-y-1.5">
            <span className="eyebrow">{t("admin_panel.vendors.field_email")}</span>
            <input
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              data-testid="vendor-form-email"
              className="orbit-input"
            />
          </label>
          <label className="space-y-1.5">
            <span className="eyebrow">{t("admin_panel.vendors.field_phone")}</span>
            <input
              type="tel"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              className="orbit-input"
            />
          </label>
          <label className="sm:col-span-2 space-y-1.5">
            <span className="eyebrow">{t("admin_panel.vendors.field_address")}</span>
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="orbit-input"
            />
          </label>
          <label className="sm:col-span-2 space-y-1.5">
            <span className="eyebrow">{t("admin_panel.vendors.field_notes")}</span>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="orbit-input"
            />
          </label>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost !py-2 !px-5 !text-xs">
            {t("admin_panel.vendors.cancel")}
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="vendor-form-save"
            className="btn-primary !py-2 !px-5 !text-xs"
          >
            {saving ? t("admin_panel.vendors.saving") : t("admin_panel.vendors.save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function CollabComposer({ vendor, onClose, onSaved, t, kindOptions }) {
  const [form, setForm] = useState({
    kind: "meal_delivery",
    title: "",
    description: "",
    scheduled_at: "",
    duration_minutes: 60,
    cost_amount: "",
    cost_currency: "AED",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/collaborations", {
        vendor_id: vendor.id,
        kind: form.kind,
        title: form.title.trim(),
        description: form.description.trim() || null,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_minutes: Number(form.duration_minutes) || null,
        cost_amount: form.cost_amount ? Number(form.cost_amount) : null,
        cost_currency: form.cost_currency,
      });
      toast.success(t("admin_panel.collaborations.toast_scheduled"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("admin_panel.collaborations.toast_schedule_failed"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" data-testid="collab-composer">
      <form onSubmit={submit} className="bg-white rounded-3xl w-full max-w-lg p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif text-2xl text-[#171717]">
              {t("admin_panel.collaborations.schedule_with", { vendor: vendor.name })}
            </h3>
            <p className="text-xs text-[#525252] mt-1">{vendor.contact_name || vendor.contact_email || ""}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t("admin_panel.vendors.close")}>
            <X size={18} />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="space-y-1.5">
            <span className="eyebrow">{t("admin_panel.collaborations.field_kind")}</span>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="orbit-input bg-transparent"
            >
              {kindOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="eyebrow">{t("admin_panel.collaborations.field_duration")}</span>
            <input
              type="number"
              min={5}
              max={600}
              value={form.duration_minutes}
              onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
              className="orbit-input"
            />
          </label>
          <label className="sm:col-span-2 space-y-1.5">
            <span className="eyebrow">{t("admin_panel.collaborations.field_title")}</span>
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="collab-form-title"
              className="orbit-input"
            />
          </label>
          <label className="sm:col-span-2 space-y-1.5">
            <span className="eyebrow">{t("admin_panel.collaborations.field_when")}</span>
            <input
              type="datetime-local"
              required
              value={form.scheduled_at}
              onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
              data-testid="collab-form-when"
              className="orbit-input"
            />
          </label>
          <label className="space-y-1.5">
            <span className="eyebrow">{t("admin_panel.collaborations.field_cost")}</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.cost_amount}
              onChange={(e) => setForm({ ...form, cost_amount: e.target.value })}
              className="orbit-input"
            />
          </label>
          <label className="space-y-1.5">
            <span className="eyebrow">{t("admin_panel.collaborations.field_currency")}</span>
            <select
              value={form.cost_currency}
              onChange={(e) => setForm({ ...form, cost_currency: e.target.value })}
              className="orbit-input bg-transparent"
            >
              {["AED", "USD", "EUR", "GBP", "SAR"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="sm:col-span-2 space-y-1.5">
            <span className="eyebrow">{t("admin_panel.collaborations.field_description")}</span>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="orbit-input"
            />
          </label>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost !py-2 !px-5 !text-xs">
            {t("admin_panel.vendors.cancel")}
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="collab-form-save"
            className="btn-primary !py-2 !px-5 !text-xs"
          >
            {saving ? t("admin_panel.collaborations.scheduling") : t("admin_panel.collaborations.schedule_cta")}
          </button>
        </div>
      </form>
    </div>
  );
}

function CollabList({ collabs, upcoming, t, statusLabel }) {
  if (collabs.length === 0) {
    return (
      <AdminEmpty testid="admin-collabs-empty">
        {t("admin_panel.collaborations.empty_state")}
      </AdminEmpty>
    );
  }
  return (
    <div className="space-y-6">
      {upcoming.length > 0 && (
        <section data-testid="admin-collabs-upcoming">
          <h3 className="eyebrow mb-3">{t("admin_panel.collaborations.section_upcoming")}</h3>
          <ul className="rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6">
            {upcoming.map((c) => (
              <li key={c.id} className="px-5 py-4">
                <div className="flex items-baseline flex-wrap gap-2">
                  <span className="font-serif text-base text-[#171717]">{c.title}</span>
                  <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full ${STATUS_PALETTE[c.status] || ""}`}>
                    {statusLabel(c.status)}
                  </span>
                </div>
                <div className="text-xs text-[#525252] mt-1">
                  {c.vendor_name} · {fmtWhen(c.scheduled_at)}
                  {c.cost_amount != null && ` · ${c.cost_currency} ${c.cost_amount.toFixed(2)}`}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section>
        <h3 className="eyebrow mb-3">{t("admin_panel.collaborations.section_all")}</h3>
        <ul className="rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6">
          {collabs.map((c) => (
            <li key={c.id} className="px-5 py-4">
              <div className="flex items-baseline flex-wrap gap-2">
                <span className="font-serif text-base text-[#171717]">{c.title}</span>
                <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full ${STATUS_PALETTE[c.status] || ""}`}>
                  {statusLabel(c.status)}
                </span>
              </div>
              <div className="text-xs text-[#525252] mt-1">
                {c.vendor_name} · {fmtWhen(c.scheduled_at)}
                {c.cost_amount != null && ` · ${c.cost_currency} ${c.cost_amount.toFixed(2)}`}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
